// ============================================================================
// Safe Care - Formulaire de souscription client
// Module : features/assistance_sociale/pages
// Charte : .tab / .formTitle / .bG / .bt_add / .tabItem / InputForm / SearchInput
// ============================================================================

import { useState, useEffect } from 'react';
import { Form, Button, Spin, Tabs, Space, Select, Table, Input } from 'antd';
import type { TabsProps } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Box, Typography } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import SweetAlert from 'sweetalert2';
import { useAuthHeader, useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import InputForm from '../../../Components/InputT';
import SearchModal from '../../../Components/SearchModal';
import Params from '../../../Types/ParamsPicklist';
import {
  useGetProduitById,
  useGetPublishedVersion,
  useGetCotisation,
  useCreateDemandeSouscription,
  useGetComptesClientByPlans,
  useGetAllDemandesSouscription,
  useApercuFrais,
} from '../hooks';
import { DemandeSouscription } from '../types';

// Picklist client (table CLIENT existante)
const searchClient: Params = {
  CodeAgence: '',
  Table: 'CLIENT',
  Colonne1: 'NUMCLI',
  Colonne2: 'NOMCLI',
  Colagence: '101',
  Choix: 22,
};

const SouscriptionForm = () => {
  const { idProduit } = useParams<{ idProduit: string }>();
  const navigate = useNavigate();
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
  const authHeader = useAuthHeader();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [numcliSelected, setNumcliSelected] = useState<string>('');

  const getUserCode = () => {
    const u = auth();
    const raw = u?.coduti ?? (u as any)?.CodUti ?? (u as any)?.CODUTI ?? localStorage.getItem('codUti') ?? sessionStorage.getItem('codUti') ?? '';
    const cleaned = String(raw).trim();
    if (!cleaned || cleaned.toLowerCase() === 'undefined' || cleaned.toLowerCase() === 'null') return '';
    return cleaned;
  };

  // Etats des boutons d'action (charte cSAL)
  const [ajoutActve, setAjoutActif] = useState(false);
  const [modifActve, setModifActve] = useState(true);
  const [enregActve, setEnregActve] = useState(true);
  const [supActive, setSupActive] = useState(true);
  const [impActive, setImpActive] = useState(true);
  const [actuActive, setActActive] = useState(true);
  const [annulActive, setAnnulActive] = useState(true);
  const [consulActive, setConsulActive] = useState(false);

  const produitId = idProduit ? parseInt(idProduit) : undefined;
  const { data: produitRes } = useGetProduitById(produitId);
  const { data: versionRes } = useGetPublishedVersion(produitId);
  const { data: cotisationRes } = useGetCotisation(
    versionRes?.StatutCode === 200 ? versionRes.Result?.Table1?.[0]?.IdVersion : undefined
  );

  // Récupérer les comptes du client filtrés par les plans de la version publiée
  const idVersionPubliee = versionRes?.StatutCode === 200 ? versionRes.Result?.Table1?.[0]?.IdVersion : undefined;
  const { data: comptesRes, isLoading: loadingComptes } = useGetComptesClientByPlans(
    numcliSelected,
    idVersionPubliee
  );
  const comptesClient = comptesRes?.StatutCode === 200 ? comptesRes.Result?.Table1 ?? [] : [];

  const createDemande = useCreateDemandeSouscription();
  const { data: rejetees, isLoading: loadingRejetees } = useGetAllDemandesSouscription('REJETEE');

  // Frais de souscription : grille du code opération global appliquée à la
  // cotisation de la version publiée ; débités automatiquement à la validation.
  const { data: apercuFraisRes, isLoading: loadingFrais } = useApercuFrais('SOUSCRIPTION', { idVersion: idVersionPubliee });
  const apercuFrais = apercuFraisRes?.Result?.Table1?.[0];
  const fraisCalcules = apercuFraisRes?.StatutCode === 200;
  const fraisMontant = Number(apercuFrais?.Frais) || 0;

  const produit = produitRes?.StatutCode === 200 ? produitRes.Result?.Table1?.[0] : null;
  const version = versionRes?.StatutCode === 200 ? versionRes.Result?.Table1?.[0] : null;
  const cotisation = cotisationRes?.StatutCode === 200 ? cotisationRes.Result?.Table1?.[0] : null;

  useEffect(() => {
    const freqMap: Record<string, string> = {
      M: 'Mensuelle',
      B: 'Bimestrielle',
      T: 'Trimestrielle',
      S: 'Semestrielle',
      A: 'Annuelle',
    };
    if (cotisation) {
      const codeFreq = cotisation.CodeFrequence ?? '';
      form.setFieldValue('MontantCotisation', cotisation.Montant);
      form.setFieldValue('CodeFrequence', codeFreq);
      form.setFieldValue('LibelleFrequence', codeFreq ? (freqMap[codeFreq] ?? codeFreq) : '');
    }
    if (version) {
      form.setFieldValue('NumeroVersion', version.NumeroVersion);
      form.setFieldValue('DureeContratMois', version.DureeContratMois);
    }
    if (produit) {
      form.setFieldValue('NomProduit', produit.Nom);
    }
  }, [cotisation, version, produit, form]);

  function initialiazeButton() {
    setAjoutActif(false);
    setModifActve(true);
    setEnregActve(true);
    setSupActive(true);
    setImpActive(true);
    setActActive(true);
    setAnnulActive(true);
    setConsulActive(false);
  }

  function SetStart() {
    setAjoutActif(true);
    setEnregActve(false);
    setAnnulActive(false);
    setConsulActive(true);
    setModifActve(true);
    // Ne pas resetFields() ici pour conserver les données du produit/version/cotisation
    // déjà préchargées. L'utilisateur n'a qu'à saisir client/compte/date.
  }

  function handleAnulle() {
    form.resetFields();
    initialiazeButton();
    setNumcliSelected('');
    navigate('/assurance/creation');
  }

  // Handler quand un client est sélectionné via la picklist
  function handleClientSelect(code: string, libelle: string) {
    setNumcliSelected(code ?? '');
    form.setFieldValue('NomClient', libelle ?? '');
  }

  const onFinish = (values: any) => {
    const codeUtilisateur = getUserCode();
    if (!codeUtilisateur) {
      SweetAlert.fire({
        title: 'Session invalide',
        text: 'Impossible de récupérer le code utilisateur (codUti). Veuillez vous reconnecter.',
        icon: 'error',
      });
      return;
    }
    // Vérifier qu'un client est sélectionné
    if (!values.Numcli) {
      SweetAlert.fire({
        title: 'Client requis',
        text: 'Vous devez sélectionner un client avant de souscrire.',
        icon: 'warning',
      });
      return;
    }
    // Vérifier qu'un compte de prélèvement est sélectionné
    if (!values.NumCptDebit) {
      SweetAlert.fire({
        title: 'Compte requis',
        text: 'Vous devez sélectionner un compte de prélèvement. Si aucun compte n\'apparaît, ce client n\'a pas de compte éligible.',
        icon: 'warning',
      });
      return;
    }
    SweetAlert.fire({
      title: 'Souscription Safe Care',
      text: `Voulez-vous souscrire au produit ${produit?.Nom} pour le client ${values.NomClient ?? values.Numcli} ?`
        + (fraisCalcules && fraisMontant > 0
          ? ` Les frais de souscription (${fraisMontant.toLocaleString('fr-FR')} FCFA) seront débités du compte ${values.NumCptDebit} à la validation.`
          : ''),
      showCancelButton: true,
      confirmButtonText: 'Oui',
      cancelButtonText: 'Non',
      color: '#116599',
      confirmButtonColor: '#116599',
      cancelButtonColor: 'darkred',
      icon: 'question',
    }).then((res) => {
      if (res.isConfirmed) {
        setLoading(true);
        const demande: DemandeSouscription = {
          Numcli: values.Numcli,
          IdProduit: produitId!,
          NumeroVersion: values.NumeroVersion,
          NumCptDebit: values.NumCptDebit,
          CodeFrequence: values.CodeFrequence,
          MontantCotisation: values.MontantCotisation,
          Statut: 'EN_ATTENTE',
          UtiSai: codeUtilisateur,
        };
        createDemande.mutate(demande, {
          onSuccess: (res) => {
            setLoading(false);
            if (res.StatutCode === 200) {
              SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
              navigate('/assurance/creation');
            } else {
              SweetAlert.fire({ title: 'Attention', text: res.Message, icon: 'warning' });
            }
          },
          onError: (err: any) => {
            setLoading(false);
            SweetAlert.fire({ title: 'Erreur', text: err?.message ?? 'Erreur', icon: 'error' });
          },
        });
      }
    });
  };

  const onFinishFailed = () => {
    SweetAlert.fire({
      title: 'Veuillez remplir tous les champs obligatoires',
      icon: 'warning',
    });
  };

  const RejeteesContent = () => {
    const columnsRejetees: ColumnsType<DemandeSouscription> = [
      { title: 'Client', dataIndex: 'Numcli', width: 100 },
      { title: 'Nom', dataIndex: 'NOMCLI', width: 140 },
      { title: 'Prénom', dataIndex: 'PRECLI', width: 140 },
      { title: 'Produit', dataIndex: 'NomProduit', width: 160 },
      { title: 'Version', dataIndex: 'NumeroVersion', width: 80 },
      { title: 'Cotisation', dataIndex: 'MontantCotisation', width: 100, align: 'right' },
      { title: 'Date effet', dataIndex: 'DateEffetSouhaitee', width: 120 },
      { title: 'Motif rejet', dataIndex: 'MotifRejet', ellipsis: true },
      { title: 'Statut', dataIndex: 'Statut', width: 100 },
    ];

    return (
      <Box className="tabItem" sx={{ p: 2 }}>
        <Box className="border border-left" sx={{ p: 2, mb: 2 }}>
          <legend style={{ marginBottom: 12 }}>Demandes de souscription rejetées</legend>
          <Spin spinning={loadingRejetees}>
            <Table
              size="small"
              rowKey="IdDemande"
              columns={columnsRejetees}
              dataSource={rejetees ?? []}
              scroll={{ y: 250, x: 10 }}
              pagination={{ defaultPageSize: 5, pageSizeOptions: [5, 10, 20, 50], showSizeChanger: true, position: ['bottomRight'] }}
            />
          </Spin>
        </Box>
      </Box>
    );
  };

  console.log('[DEBUG SouscriptionForm] rejetees:', rejetees, 'length:', rejetees?.length);

  let items: TabsProps['items'] = [
    {
      key: '1',
      label: 'Souscription',
      children: (
        <div className="tabItem">
          <div>
            <Box fontStyle="inherit" className="border border-left" width="85%" position="absolute" sx={{ p: 3 }}>
              <legend style={{ marginBottom: 12 }}>Souscription Safe Care</legend>
              <Box display="flex" fontStyle="inherit" sx={{ mb: 1, gap: 3 }}>
                <Box fontStyle="inherit" sx={{ flex: 1 }}>
                  <InputForm width={9} label="Produit" name="NomProduit" type="label" />
                  <InputForm width={9} label="Version" name="NumeroVersion" type="label" />
                  {/* Picklist client (table CLIENT) - remplace user.coduti */}
                  <SearchModal
                    width={12}
                    Titre=""
                    label="Client (NUMCLI)"
                    name="Numcli"
                    Parametre={searchClient}
                    Taille1={167}
                    Taille2={320}
                    form={form}
                    handleFunction={handleClientSelect}
                  />
                  <InputForm width={9} label="Nom client" name="NomClient" type="label" />
                  <Form.Item
                    name="NumCptDebit"
                    label="Compte de prélèvement"
                    rules={[{ required: true, message: 'Compte de prélèvement obligatoire' }]}
                  >
                    <Select
                      loading={loadingComptes}
                      placeholder={numcliSelected ? 'Sélectionnez le compte' : 'Sélectionnez d\'abord un client'}
                      notFoundContent={numcliSelected ? 'Aucun compte éligible pour ce client' : 'Sélectionnez d\'abord un client'}
                      showSearch
                      optionFilterProp="children"
                      disabled={!numcliSelected}
                    >
                      {comptesClient.map((c: any) => (
                        <Select.Option key={c.NUMCPT} value={c.NUMCPT}>
                          {c.NUMCPT} — {c.NOMCPT}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {numcliSelected && comptesClient.length === 0 && !loadingComptes && (
                    <Typography variant="caption" style={{ color: '#d32f2f', display: 'block', marginBottom: 8 }}>
                      Votre compte n'appartient à aucun plan comptable ajouté par l'admin pour ce Safe Care.
                    </Typography>
                  )}
                  <Form.Item name="CodeFrequence" hidden>
                    <input type="hidden" />
                  </Form.Item>
                  <InputForm
                    width={9}
                    label="Fréquence cotisation"
                    name="LibelleFrequence"
                    type="label"
                  />
                </Box>
                <Box fontStyle="inherit" sx={{ flex: 1 }}>
                  {/* Montant imposé par REGLECOTISATION de la version : lecture seule (contrôlé aussi côté serveur) */}
                  <InputForm
                    width={9}
                    money
                    label="Montant cotisation"
                    name="MontantCotisation"
                    type="number"
                    disabled
                  />
                  {/* Frais de souscription : calculés par la grille du code opération
                      sur la cotisation de la version, débités à la validation. */}
                  <Form.Item label="Frais de souscription" labelCol={{ span: 9 }}>
                    <Input
                      readOnly
                      style={{ width: 160 }}
                      value={loadingFrais ? 'Calcul…' : fraisCalcules
                        ? (fraisMontant > 0 ? `${fraisMontant.toLocaleString('fr-FR')} FCFA` : 'Aucun')
                        : 'Non calculable'}
                    />
                    {!loadingFrais && apercuFraisRes && (
                      <Typography variant="caption" style={{ display: 'block', color: fraisCalcules ? undefined : '#d32f2f' }}>
                        {fraisCalcules
                          ? (fraisMontant > 0
                            ? `Débités automatiquement à la validation (grille ${apercuFrais?.CodTarif}, palier ${apercuFrais?.NumPalier}, base ${Number(apercuFrais?.MontantBase ?? 0).toLocaleString('fr-FR')} FCFA).`
                            : apercuFraisRes.Message)
                          : `${apercuFraisRes.Message} La validation de la demande sera refusée tant que ce paramétrage n'est pas corrigé.`}
                      </Typography>
                    )}
                  </Form.Item>
                  <InputForm
                    width={9}
                    label="Durée contrat (mois)"
                    name="DureeContratMois"
                    type="label"
                  />
                </Box>
              </Box>
            </Box>
          </div>
        </div>
      ),
    },
  ];

  if (rejetees && rejetees.length > 0) {
    items.push({
      key: '2',
      label: 'Demandes rejetées',
      children: <RejeteesContent />,
    });
  }

  return (
    <div className="tab safeCarePage">
      <Typography align="center" className="formTitle">
        <b>Souscription Safe Care{produit ? ` — ${produit.Nom}` : ''}</b>
      </Typography>
      <Spin spinning={loading} size="large" style={{ color: '#116599' }}>
        <Form
          disabled={!enregActve && !consulActive}
          autoComplete="off"
          labelAlign="left"
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          style={{ minWidth: 'auto', maxWidth: 'auto' }}
        >
          <div className="bG">
            <legend>
              <Typography color="var(--darkblue)">Actions</Typography>
            </legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={consulActive}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Consulter
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={ajoutActve}
                  onClick={SetStart}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Ajouter
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={modifActve}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Modifier
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={enregActve}
                  type="ghost"
                  htmlType="submit"
                  block
                >
                  Enregistrer
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={supActive}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Supprimer
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={impActive}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Imprimer
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={actuActive}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Actualiser
                </Button>
                <Button
                  size="large"
                  disabled={annulActive}
                  onClick={handleAnulle}
                  className="bt_add"
                  type="ghost"
                  htmlType="reset"
                  block
                >
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className="clientP">
            <div>
              <Tabs size="small" type="card" defaultActiveKey="1" items={items} animated={{ inkBar: true, tabPane: true }} />
            </div>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default SouscriptionForm;
